import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, Translations } from './translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Default empty translations while loading
const defaultTranslations: Translations = {
  nav: { about: '', portfolio: '', terms: '', contact: '' },
  hero: { subtitle: '' },
  about: { title: '', paragraph1: '', paragraph2: '', paragraph3: '' },
  portfolio: { title: '', description: {paragraph1: '', paragraph2: ''}, imagePlaceholder: '', ctaButton: '' },
  terms: { title: '', subtitle: '', benefitsLabel: '', services: [] },
  contact: {
    title: '',
    description: '',
    form: {
      name: '',
      namePlaceholder: '',
      nameError: '',
      email: '',
      emailPlaceholder: '',
      emailError: '',
      message: '',
      messagePlaceholder: '',
      messageError: '',
      submit: '',
      sending: '',
      successMessage: '',
      errorMessage: '',
      recaptchaError: ''
    }
  },
  footer: { rights: '' },
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<Language>(() => {
    const saved = localStorage.getItem('language');
    return (saved === 'en' || saved === 'uk') ? saved as Language : 'en';
  });

  const [translations, setTranslations] = useState<Translations>(defaultTranslations);

  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const response = await fetch(`/translations/${language}.json`);
        const data = await response.json();
        setTranslations(data);
      } catch (error) {
        console.error('Failed to load translations:', error);
      }
    };

    loadTranslations();
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};